import { useCallback, useMemo } from 'react';

import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { useReadSelectedAreas } from '@/modules/floorplanDesign/hooks';
import { areasToFlatCollection, isStationaryArea } from '@/modules/floorplanDesign/helpers';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';

export const Extensions = () => {
  const selectedAreas = useReadSelectedAreas();
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const extensions = useMemo(() => {
    const areas = areasToFlatCollection(selectedAreas);
    if (areas.length !== 1) return;
    const area = areas[0];
    if (!isStationaryArea(area)) return;

    const { parkingExtension, chargingExtension, insertionExtension, extractionExtension } = area;
    return { parkingExtension, chargingExtension, insertionExtension, extractionExtension };
  }, [selectedAreas]);

  const stationaryType = useMemo(() => {
    const areas = areasToFlatCollection(selectedAreas);
    if (areas.length !== 1) return;
    const area = areas[0];
    if (!isStationaryArea(area)) return;

    const { stationaryType } = area;
    return stationaryType;
  }, [selectedAreas]);

  const onParkingPriorityChange = useCallback(
    (newValue: number) => {
      const areas = areasToFlatCollection(selectedAreas);
      if (areas.length !== 1) return;
      const area = areas[0];
      if (!isStationaryArea(area)) return;

      if (Number.isNaN(newValue)) return;

      fddmSvc.updateArea({
        ...area,
        parkingExtension: {
          ...area.parkingExtension,
          priority: newValue,
        },
      });
    },
    [selectedAreas, fddmSvc],
  );

  const onChargingPriorityChange = useCallback(
    (newValue: number) => {
      const areas = areasToFlatCollection(selectedAreas);
      if (areas.length !== 1) return;
      const area = areas[0];
      if (!isStationaryArea(area)) return;

      if (Number.isNaN(newValue)) return;

      fddmSvc.updateArea({
        ...area,
        chargingExtension: {
          ...area.chargingExtension,
          priority: newValue,
        },
      });
    },
    [selectedAreas, fddmSvc],
  );

  const onInsertionDurationChange = useCallback(
    (newValue: number) => {
      const areas = areasToFlatCollection(selectedAreas);
      if (areas.length !== 1) return;
      const area = areas[0];
      if (!isStationaryArea(area)) return;

      if (Number.isNaN(newValue)) return;

      fddmSvc.updateArea({
        ...area,
        insertionExtension: {
          ...area.insertionExtension,
          waitDurationInSimulation: newValue,
        },
      });
    },
    [selectedAreas, fddmSvc],
  );

  const onExtractionDurationChange = useCallback(
    (newValue: number) => {
      const areas = areasToFlatCollection(selectedAreas);
      if (areas.length !== 1) return;
      const area = areas[0];
      if (!isStationaryArea(area)) return;

      if (Number.isNaN(newValue)) return;

      fddmSvc.updateArea({
        ...area,
        extractionExtension: {
          ...area.extractionExtension,
          waitDurationInSimulation: newValue,
        },
      });
    },
    [selectedAreas, fddmSvc],
  );

  if (extensions === undefined) return null;

  return (
    <>
      {stationaryType === 'PARKING' && (
        <PropertiesLabel i18nkey='interface:properties.area.priority'>
          <NumberInput
            value={extensions.parkingExtension?.priority ?? 0}
            sx={{ width: '100%' }}
            minValue={0}
            onChange={onParkingPriorityChange}
          />
        </PropertiesLabel>
      )}
      {stationaryType === 'CHARGING' && (
        <PropertiesLabel i18nkey='interface:properties.area.priority'>
          <NumberInput
            value={extensions.chargingExtension?.priority ?? 0}
            sx={{ width: '100%' }}
            minValue={0}
            onChange={onChargingPriorityChange}
          />
        </PropertiesLabel>
      )}
      {stationaryType === 'INSERTION' && (
        <PropertiesLabel i18nkey='interface:properties.area.block_duration'>
          <NumberInput
            value={extensions.insertionExtension?.waitDurationInSimulation ?? 0}
            unit='sec'
            sx={{ width: '100%' }}
            minValue={0}
            onChange={onInsertionDurationChange}
          />
        </PropertiesLabel>
      )}
      {stationaryType === 'EXTRACTION' && (
        <PropertiesLabel i18nkey='interface:properties.area.block_duration'>
          <NumberInput
            value={extensions.extractionExtension?.waitDurationInSimulation ?? 0}
            unit='sec'
            sx={{ width: '100%' }}
            minValue={0}
            onChange={onExtractionDurationChange}
          />
        </PropertiesLabel>
      )}
    </>
  );
};
